import React from 'react';
import './Checkin/styles.css';
import {
  Flex,
  IconCheckMarkSolid,
  IconXSolid,
  IconArrowOpenDownSolid,
  IconPlayLine,
  DrawerLayout,
  CloseButton,
  View,
  Tray,
} from '@smarterservices/smarter-ui';
import { Menu, Button, Mask } from '@instructure/ui';
import { useEffect, useState, useRef } from 'react';
import CheckinItem from './Checkin/Item';
import CheckinGroup from './Checkin/Group';
import PromptApproval from './Checkin/PromptApproval';
import { getStore, useCheckinStore } from '../../stores/StoreContext';
import LocalTray from '../Common/SlideTray';

const Checkin = () => {
  const CheckinStore = getStore('checkinStore');
  const PlayerStore = getStore('playerStore');
  const LayoutStore = getStore('layoutStore');

  const [loaded, setLoaded] = useState(false);
  const [promptApproval, setPromptApproval] = useState(false);
  const [openTray, setOpenTray] = useState(false);
  const { setTrayHeight } = useCheckinStore();

  // Subscribe to the specific value from the store
  const checkinActive = useCheckinStore((state) => state.checkinActive);
  const allowApproval = useCheckinStore((state) => state.allowApproval);
  const inSequentialFlow = useCheckinStore((state) => state.inSequentialFlow);
  const isApproved = useCheckinStore((state) => state.isApproved);
  const trayHeight = useCheckinStore((state) => state.trayHeight);
  const hasUnreviewedItems = useCheckinStore(
    (state) => state.hasUnreviewedItems
  );

  const checkinDrawerRef = useRef(null);

  useEffect(() => {
    const loadItems = async () => {
      await CheckinStore.initialize();
      setLoaded(true);
    };
    loadItems();
  }, []);

  useEffect(() => {
    const calculateCheckinTraySize = () => {
      if (checkinActive && checkinDrawerRef.current) {
        const rect = checkinDrawerRef.current.getBoundingClientRect();
        const session =
          LayoutStore.containers?.session?.element.getBoundingClientRect();
        const height = session.height - rect.top;

        setTrayHeight(height);
      }
    };

    if (checkinActive) {
      calculateCheckinTraySize();

      setOpenTray(true);
    } else {
      setOpenTray(false);
      setTrayHeight(0);
    }

    window.addEventListener('resize', calculateCheckinTraySize);

    // Cleanup function to remove listener on unmount
    return () => {
      window.removeEventListener('resize', calculateCheckinTraySize);
      setOpenTray(false);
      if (checkinActive) {
        CheckinStore.clearActiveItem();
      }
    };
  }, [checkinActive]);

  const handleGetNextItem = () => {
    return CheckinStore.getNextItem();
  };

  /**
   * Handles the completion of the current item.
   * If in sequential flow or approval is allowed, it sets the prompt for approval.
   * Otherwise, it clears the active item.
   */
  const handleComplete = () => {
    if (inSequentialFlow || CheckinStore.allowApproval) {
      const activeItem = CheckinStore.activeItem;
      setPromptApproval(true);
    } else {
      CheckinStore.clearActiveItem();
    }
  };

  const handleApproval = () => {
    CheckinStore.approveSession();
  };

  const renderApprovalControl = (props = {}) => {
    if (!props?.placement) {
      props.placement = 'bottom';
    }

    return (
      <>
        {!isApproved && (
          <Flex justifyItems="space-between" margin={props?.margin}>
            <Flex.Item>
              {hasUnreviewedItems && (
                <Button
                  color="success"
                  renderIcon={IconPlayLine}
                  onClick={() => {
                    CheckinStore.setInSequentialFlow(true);
                    const item = handleGetNextItem();
                    CheckinStore.setActiveItem(item);
                  }}
                >
                  Start
                </Button>
              )}
            </Flex.Item>
            <Flex.Item>
              <Menu
                placement={props.placement}
                trigger={
                  <Button color="primary">
                    Session Approval <IconArrowOpenDownSolid />{' '}
                  </Button>
                }
              >
                <Menu.Item
                  value="approve"
                  //TODO: commented the item below out so we can move people through VTC...
                  //disabled={!allowApproval}
                  onClick={CheckinStore.approveSession}
                >
                  <IconCheckMarkSolid /> Approve
                </Menu.Item>
                <Menu.Item
                  value="deny"
                  onClick={() => CheckinStore.setDenyConfirmationOpen(true)}
                >
                  <IconXSolid /> Deny
                </Menu.Item>
              </Menu>
            </Flex.Item>
          </Flex>
        )}
      </>
    );
  };

  return (
    <>
      <View
        margin="none"
        padding="none"
        elementRef={(el) => {
          checkinDrawerRef.current = el;
        }}
      >
        {loaded ? (
          <>
            {renderApprovalControl({ margin: 'xx-small none none none' })}
            {Object.keys(CheckinStore.groups).map((groupId) => (
              <CheckinGroup
                key={`group-${groupId}`}
                group={CheckinStore.groups[groupId]}
              />
            ))}
          </>
        ) : (
          <>Loading</>
        )}
      </View>

      <Tray
        label="Tray Example"
        open={openTray}
        size="large"
        placement="bottom"
        mountNode={LayoutStore.containers?.session?.element}
        themeOverride={{
          largeWidth: `${trayHeight}px`,
          position: 'absolute',
        }}
        transitionEnter={false}
        transitionExit={false}
        onExit={() => {
          console.log('On Exit');
          //useCheckinStore.getState().clearActiveItem();
          CheckinStore.setInSequentialFlow(false);
          setPromptApproval(false);
        }}
      >
        <View as="div" margin="none" padding="large" height={`${trayHeight}px`}>
          <CloseButton
            placement="end"
            offset="small"
            onClick={() => CheckinStore.clearActiveItem()}
            screenReaderLabel="Close"
          />
          {!promptApproval ? (
            <CheckinItem
              getNextItem={handleGetNextItem}
              onComplete={handleComplete}
            />
          ) : (
            <PromptApproval allowApproval={allowApproval} />
          )}
        </View>
      </Tray>
    </>
  );
};

export default Checkin;
